import { Component, Input, OnInit } from '@angular/core';
import { IndPageTitleViewDataModel } from '../../../models/indPageTitleDataModel';
import { IndSectionHeaderViewDataModel } from '../../../models/indSectionHeaderDataModel';
import { NavigationService, PageName } from '../../../services/navigation/navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { SampleDeliveryRequest, getSdrStatusColor, sdrDisplayTitle } from '../../../classes/sample/sample-delivery-request.class';
import { IONote } from '../../../classes/io/io-note.class';
import { SampleDeliveryRequestDataService } from '../../../data-services/sample/sample-delivery-request.data.service';
import { SdrNoteSavedPayload } from '../sdr-notes-section/sdr-notes-section.component';
import { NotificationService, ToastStyle } from '../../../services/notification/notification.service';
import { DeviceService } from '../../../services/device/device.service';
import { UIService } from '../../../services/ui/ui.service';
import { SecInfoConfigDataService } from '../../../data-services/sec-info-config/sec-info-config-data-service';
import { ContactOfflineService } from '../../../services/contact/contact.service';
import { Contact } from '../../../classes/contact/contact.class';

@Component({
  selector: 'sample-delivery-request-detail',
  templateUrl: 'sample-delivery-request-detail.component.html',
  styleUrls: ['sample-delivery-request-detail.component.scss'],
})
export class SampleDeliveryRequestDetailComponent implements OnInit {
  @Input() sdr: SampleDeliveryRequest;
  @Input() from: PageName;
  /**
   * Notes pre-loaded from the submit batch (warm path from the create form) or
   * a contact-timeline fetch. Falls back to `sdr.notes` so a navigator that
   * only passes `sdr` still gets the link-entity notes attached at fetch time.
   */
  @Input() notes: IONote[] = [];

  /** Shown while the full record is fetched on open (timeline-summary path). */
  isLoading = false;

  get pageTitleData(): IndPageTitleViewDataModel {
    // On web the notification list is always visible in the left pane, so a back
    // button inside the detail pane is redundant. On mobile we keep it — using
    // push-nav means back correctly returns to the notification list.
    const showBack = !(this.from === PageName.NotificationListComponent && !this.device.isMobileDevice);
    return {
      id: 'sdr-detail-page-title',
      // `??` would not catch an empty autonumber (`title === ''`); sdrDisplayTitle
      // falls back to the generic label for empty/whitespace titles too.
      title: sdrDisplayTitle(
        this.secInfoConfigDataService.stripSdrTitleTrailingSeparator(this.sdr?.title),
        this.translate.instant('SAMPLE_DELIVERY_REQUEST'),
        // Customer name, not the id — see sdrDisplayTitle (OMNI-61543).
        this.customerName,
      ),
      controls: showBack
        ? [
            {
              id: 'close',
              icon: 'chevron-back-outline',
              isDisabled: false,
              align: 'left',
            },
          ]
        : [],
    };
  }

  /**
   * "Details" section header with the SDR status shown as a colour-coded pill on
   * the right — mirrors the meeting detail pane (`activities-details-pane`).
   * The label is the raw `statuscode` FormattedValue (`statusLabel`), so any
   * future optionset value renders correctly; only the pill colour is mapped
   * (delivered = green, everything else = blue for now — see `getSdrStatusColor`).
   */
  get detailsHeader(): IndSectionHeaderViewDataModel {
    // An offline-created/finalized record has no statuscode FormattedValue to
    // parse a label from (no offline write path stamps one), so `statusLabel` is
    // '' and the pill vanished entirely. Show the sync state instead — it is the
    // truthful status for a record that has not reached Dynamics yet, and it
    // matches the badge the in-meeting inline view already shows (OMNI-61603).
    const statusLabel = this.sdr?.pendingSync
      ? this.translate.instant('PENDING_SYNC')
      : (this.sdr?.statusLabel || '');
    return {
      id: 'sdr-detail-details-header',
      title: this.translate.instant('DETAILS'),
      controls: [],
      rightLabelTxt: statusLabel || undefined,
      customRightLabelClass: statusLabel
        ? `sdr-details-status-badge sdr-details-status--${getSdrStatusColor(this.sdr?.statusCode)}`
        : undefined,
    };
  }

  /**
   * The customer name to render. Falls back to resolving `contactId` against the
   * local contact store: an offline-created SDR carries the id but no name (the
   * name comes from the customer lookup's FormattedValue, which only a server
   * round-trip supplies), and the offline branch of ngOnInit merges notes only,
   * so nothing else ever fills it in. Contacts are in the offline store, so this
   * resolves with no network (OMNI-61603).
   */
  get customerName(): string {
    if (this.sdr?.contactName) return this.sdr.contactName;
    return this._resolveContact(this.sdr?.contactId)?.fullName || '';
  }

  /**
   * Full composite text for a saved (looked-up) delivery address. The stored
   * `deliveryAddressName` is the lookup's FormattedValue — the address record's
   * NAME ("Mathikere") rather than the composite line the rep picked — so the
   * composite is re-resolved from the contact's address list by id, with the
   * stored name as a last resort (OMNI-62537). A free-text alternate wins, as
   * everywhere else.
   */
  get deliveryAddressText(): string {
    if (this.sdr?.alternateAddress) return this.sdr.alternateAddress;
    const contact = this._resolveContact(this.sdr?.contactId);
    const composite = (contact?.addressesList ?? [])
      .find((a) => a.addressId === this.sdr?.deliveryAddress)?.compositeAdd;
    return composite || this.sdr?.deliveryAddressName || this.translate.instant('NO_DELIVERY_ADDRESS');
  }

  /** Memoised: getContactByID is a linear scan over the whole contact array and
   *  these getters are read from the template on every change-detection pass. */
  private _contactCache: { id: string; contact: Contact | undefined } | null = null;

  private _resolveContact(contactId: string): Contact | undefined {
    if (!contactId) return undefined;
    if (this._contactCache?.id !== contactId) {
      this._contactCache = { id: contactId, contact: this.contactService.getContactByID(contactId) };
    }
    return this._contactCache.contact;
  }

  get contactInitials(): string {
    return this.customerName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  constructor(
    private readonly navService: NavigationService,
    private readonly translate: TranslateService,
    private readonly dataService: SampleDeliveryRequestDataService,
    private readonly notificationService: NotificationService,
    private readonly device: DeviceService,
    private readonly uiService: UIService,
    private readonly secInfoConfigDataService: SecInfoConfigDataService,
    private readonly contactService: ContactOfflineService,
  ) {}

  async ngOnInit() {
    // Slim summaries (timeline `fetchSdrsForContact`, the device-wide `allSdrs`
    // cache, and notification payloads) arrive WITHOUT notes/attachments — only
    // the full-detail (`fetchSdrById`) and create paths carry them. We gate on
    // `notesLoaded`, NOT on `contactId`: every entry path except those two now
    // carries `contactId`, so the old `!contactId` heuristic skipped the fetch
    // for `allSdrs`/notification/warm-create objects and notes never loaded —
    // offline OR online from a notification (OMNI-57234).
    if (this.sdr?.id && !this.sdr.notesLoaded) {
      this.isLoading = true;
      try {
        if (this.device.isOffline) {
          // Offline: serve notes from the lazy detail cache if this SDR was ever
          // opened online (or created on-device). MERGE — never replace: keep the
          // fresh summary fields (status/address/email/products) already on
          // `this.sdr` from the just-synced object the caller handed in, and graft
          // only notes/attachments, which live solely in the detail cache. A
          // wholesale replace would clobber a freshly-synced status with the
          // cache's stale one. Never hits the network.
          const cached = await this.dataService.getCachedSdrRecord(this.sdr.id);
          if (cached) {
            this.sdr.notes = cached.notes ?? [];
            this.sdr.notesLoaded = true;
            this.notes = cached.notes ?? [];
          }
        } else {
          // Online: always fetch network truth (and refresh the cache). A
          // status-change notification means the record changed server-side, so
          // trusting a cache hit here would risk showing stale status/notes.
          const full = await this.dataService.fetchSdrById(this.sdr.id);
          if (full) {
            this.sdr = full;
            this.notes = full.notes ?? [];
            await this.dataService.saveSdrRecordToDb(full); // lazy notes capture
          }
        }
      } catch (err) {
        console.error('SDR detail fetch failed', err);
        if (!this.device.isOffline) {
          this.toast('ERROR_SDR_SUBMIT_FAILED', ToastStyle.DANGER);
        }
      } finally {
        this.isLoading = false;
      }
    }
    if (!this.notes?.length && this.sdr?.notes?.length) {
      this.notes = this.sdr.notes;
    }
  }

  // `ind-page-title.onControlClick` emits the clicked control's id as a raw
  // string — see the matching note in `new-sample-delivery-request.component.ts`.
  onPageTitleControlClick(id: string) {
    if (id !== 'close') return;
    if (this.from === PageName.NotificationListComponent) {
      // Opened from the notifications page. The notification list is the left
      // (master) pane and the SDR detail is the right-pane child nav. To return
      // to the list we reset the right pane to the placeholder and clear the
      // right-pane-active flag — on mobile this collapses the right pane and
      // reveals the list (the host page re-checks via its showRightPane$ sub).
      // Gate on `from` only, NOT device.isMobileDevice: the back button is
      // already hidden on web (see pageTitleData), and the mobile layout is
      // driven by a CSS media query, not by isMobileDevice.
      this.navService.setChildNavRoot(
        PageName.NothingSelectedView,
        PageName.NothingSelectedView,
        PageName.GoAppNotificationPageComponent,
      );
      this.navService.setChildNavRightPaneView(false);
      this.uiService.showRightPane = false;
    } else {
      this.navService.popChildNavPageWithPageTracking();
    }
  }

  /**
   * SAVE path from the existing-notes list: text edit, attachment swap, or
   * attachment removal. Maps the wrapper payload to the annotation patch shape
   * BA confirmed (text-only → `notetext`; swap → full file fields;
   * remove → empty file fields + `isdocument: false`).
   */
  async onNoteSaved(payload: SdrNoteSavedPayload) {
    if (!payload?.noteId) return;
    const patch: Record<string, any> = { notetext: payload.text ?? '' };
    if (payload.attachment) {
      patch.documentbody = payload.attachment.fileDataUrl;
      patch.filename = payload.attachment.name;
      patch.mimetype = payload.attachment.mimeType;
      patch.filesize = payload.attachment.size;
      patch.isdocument = true;
    } else if (payload.isAttachmentRemoved) {
      patch.documentbody = '';
      patch.filename = '';
      patch.mimetype = '';
      patch.filesize = 0;
      patch.isdocument = false;
    }
    try {
      await this.dataService.updateNote(payload.noteId, patch);
      this.applyNoteUpdate(payload, patch);
    } catch (err) {
      console.error('SDR note update failed', err);
      this.toast('ERROR_SDR_SUBMIT_FAILED', ToastStyle.DANGER);
    }
  }

  async onNoteDeleted(noteId: string) {
    if (!noteId) return;
    try {
      await this.dataService.deleteNote(noteId);
      this.notes = this.notes.filter((n) => n.noteId !== noteId);
    } catch (err) {
      console.error('SDR note delete failed', err);
      this.toast('ERROR_SDR_SUBMIT_FAILED', ToastStyle.DANGER);
    }
  }

  private applyNoteUpdate(payload: SdrNoteSavedPayload, patch: Record<string, any>) {
    const idx = this.notes.findIndex((n) => n.noteId === payload.noteId);
    if (idx < 0) return;
    const target = this.notes[idx];
    target.noteText = patch.notetext ?? '';
    if (payload.attachment) {
      target.hasDocument = true;
      target.documentName = patch.filename;
      target.documentMimeType = patch.mimetype;
      target.documentSize = patch.filesize;
      // documentbody intentionally not kept in memory — next download tap will
      // pull it via IoFileService.getDocumentBody.
      target.documentDataURL = '';
    } else if (payload.isAttachmentRemoved) {
      target.hasDocument = false;
      target.documentName = '';
      target.documentMimeType = '';
      target.documentSize = 0;
      target.documentDataURL = '';
    }
  }

  private toast(key: string, style: ToastStyle) {
    this.notificationService.notify(
      this.translate.instant(key),
      'sample-delivery-request-detail',
      'top',
      style,
    );
  }
}
